import CustomInput from "../../../CustomInput";
import { Button, styled, InputAdornment, IconButton } from "@mui/material";
import colors from "../../../../../theme/themeConstants";
import Captcha from "../../../Captcha";
import { ChangeEvent, FormEvent, useCallback, useState } from "react";
import { login } from "services/Auth";
import { ReactComponent as EyeIcon } from "components/icons/grey-eye.svg";
import { ReactComponent as CrossEyeIcon } from "components/icons/cross-grey-eye.svg";
import { BaseButton } from "components/buttons/BaseButton";
import { AxiosError } from "axios";
import { IErrorData } from "services/types";

interface IProps {
  next: () => void;
}

const LoginWithEmail = ({ next }: IProps) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [resetCaptcha, setResetCaptcha] = useState<(() => void) | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>("");
  const [passwordError, setPasswordError] = useState<string | null>("");
  const [captchaError, setCaptchaError] = useState<string | null>("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    validate();

    if (!isValid()) {
      return;
    }

    setError("");
    setIsSending(true);

    try {
      await login({ username, password, captcha });
    } catch (e) {
      const message = (e as AxiosError<IErrorData>)?.response?.data?.error?.message || "Service unavailable";
      setError(message);
    } finally {
      setIsSending(false);
      handleCaptchaReset();
      if (resetCaptcha !== null) {
        resetCaptcha();
      }
    }
  };

  const validateUsername = useCallback((value: string) => {
    setUsernameError(value.length === 0 ? "Username required" : null);
  }, []);

  const validatePassword = useCallback((value: string) => {
    setPasswordError(value.length === 0 ? "Password required" : null);
  }, []);

  const validateCaptcha = useCallback((value: string) => {
    setCaptchaError(value.length === 0 ? "Captcha required" : null);
  }, []);

  const validate = useCallback(
    () => {
      validateUsername(username);
      validatePassword(password);
      validateCaptcha(captcha);
    },
    [
      validateUsername,
      validatePassword,
      validateCaptcha,
      username,
      password,
      captcha,
    ]
  );

  const isValid = useCallback(
    () => usernameError === null && passwordError === null && captchaError === null,
    [usernameError, passwordError, captchaError]
  );

  const handleChangeUsername = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    validateUsername(e.target.value);
  }, [validateUsername]);

  const handleChangePassword = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    validatePassword(e.target.value);
  }, [validatePassword]);

  const handleCaptchaSuccess = useCallback((token: string) => {
    setCaptcha(token);
    validateCaptcha(token);
  }, [validateCaptcha]);

  const handleCaptchaReset = useCallback(() => {
    setCaptcha("");
    validateCaptcha("");
  }, [validateCaptcha]);

  const handleCaptchaRendered = useCallback((reset: () => void) => {
    setResetCaptcha(() => reset);
  }, []);

  const handleResetPassword = () => {
    next();
  };

  return (
    <Container>
      <form onSubmit={handleSubmit}>
        <Block>
          <label>Email or User Name</label>
          <CustomInput
            value={username}
            onChange={handleChangeUsername}
            error={!!usernameError}
            helperText={usernameError ?? ""}
            fullWidth
            autoComplete={"off"}
            autoFocus={true}
          />
        </Block>

        <Block>
          <label>Password</label>
          <CustomInput
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={handleChangePassword}
            error={!!passwordError}
            helperText={passwordError ?? ""}
            fullWidth
            autoComplete={"off"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(prev => !prev)}
                    edge="end"
                  >
                    {showPassword ? <EyeIcon /> : <CrossEyeIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Button
            sx={{
              width: "fit-content",
              alignSelf: "flex-end",
              fontWeight: 500,
              fontSize: "12px",
              color: colors.textDark,
              fontFamily: "Poppins",
              opacity: 0.5,
            }}
            onClick={handleResetPassword}
          >
            Forgot password?
          </Button>
        </Block>

        <Block>
          <Captcha
            id="captcha"
            onSuccess={handleCaptchaSuccess}
            onFailed={handleCaptchaReset}
            onRendered={handleCaptchaRendered}
          />
        </Block>

        { error &&
          <Block>
            <ErrorMessage>
              { error }
            </ErrorMessage>
          </Block>
        }

        <StyledBaseButton>
          <BaseButton type="submit" disabled={!isValid() || isSending}>
            Log in
          </BaseButton>
        </StyledBaseButton>
      </form>
    </Container>
  );
};

export default LoginWithEmail;

const Container = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
});

const StyledBaseButton = styled("div")({
  ".tablet &, .phone &": {
    position: "absolute",
    bottom: "0",
    left: "0",
    width: "100%",
    padding: "16px 16px 32px",
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "24px"
  }
});

const Block = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  marginBottom: "1rem",
  "& label": {
    fontSize: "12px",
    color: colors.textDark
  }
});

const ErrorMessage = styled("div")({
  color: "red",
  textAlign: "center",
  fontWeight: 400,
  fontSize: "0.75rem",
});
